import React from 'react';
import { useSelector } from 'react-redux';
import { Route, Switch } from 'react-router';
import { RouteBoundary } from 'signer-app/parts/error-boundary';
import { useHSUser } from './common/hs-user';
import { RedirectTo } from './common/utils/redirect';
import { LabeledRoute } from './components/labeled-route';
import { usePageProps, useUnsafePageProps } from './slices/page-props';
import { useFeatureFlag } from 'js/sign-components/common/feature-flag';
import { siteCodes } from 'js/sign-components/common/brand';
import { useSiteCode } from 'js/sign-components/common/use-site-code';
import { StoreShape } from './redux/namespaces';
import { LazyHomeFaxPage } from './page/home-fax/lazy-home-fax-page';
import { LazyHomeSignPage } from './page/home-sign/lazy-home-sign-page';
import { LazyTemplateGalleryPage } from './page/template-gallery/lazy-template-gallery-page';
import { TemplateGalleryLayout } from './page/template-gallery/template-gallery-layout';
import { LazyViewGalleryTemplatePage } from './page/view-gallery-template/lazy-view-gallery-template-page';
import { LazyBrowseTemplateGalleryPage } from './page/browse-template-gallery/lazy-browse-template-gallery-page';
import { LazyTemplatesPage } from './page/templates/lazy-templates-page';
import { LazyEditorPage } from './page/editor/lazy-editor-page';
import { LazyAdminPage } from './page/admin/lazy-admin-page';
import { LazyMoarPage } from './page/moar/lazy-moar-page';
import { LazyDropboxConnectPage } from './page/dropbox/connect/lazy-dropbox-connect-page';
import { LazyDropboxErrorPage } from './page/dropbox/error/lazy-dropbox-error-page';
import { LazyDropboxSsoPage } from './page/dropbox/sso/lazy-dropbox-sso-page';
import { LazyDropboxConfirmStorageAccessPage } from './page/dropbox/confirm-storage-access/lazy-dropbox-confirm-storage-access-page';
import { LazyDropboxRequestStorageAccessPage } from './page/dropbox/request-storage-access/lazy-dropbox-request-storage-access-page';
import { LazySafetyFirstPage } from './page/safetyfirst/lazy-safety-first-page';
import { LazyGettingStartedPage } from './page/getting-started/lazy-getting-started-page';
import { LazyUserPostSignUpPage } from './page/user-post-sign-up/lazy-user-post-sign-up-page';
import { LazyRecoverPasswordPage } from './page/recover-password-page/lazy-recover-password-page';
import { LazyNothingToSignPage } from './page/nothing-to-sign-page/lazy-nothing-to-sign-page';
import { LazyQesRecoveryPage } from './page/qes-recovery/lazy-qes-recovery-page';
import { LazySetPasswordPage } from './page/set-password-page/lazy-set-password-page';
import { LazyOnboardingUpsellPage } from './page/onboarding-upsell-page/lazy-onboarding-upsell-page';
import { LazyInformationFlagPage } from './page/information-flag/lazy-information-flag-page';
import { LazyHubspotSelectTemplatePage } from './page/hubspot/select-template/lazy-hubspot-select-template-page';
import { LazyHubspotClosePage } from './page/hubspot/close/lazy-hubspot-close-page';
import { LazyIntegrationWorkflowStatusPage } from './page/integration/workflow-status/lazy-integration-workflow-status-page';
import { LazyIntegrationCreateOneTimeDocPage } from './page/integration/create-one-time-doc/lazy-integration-create-one-time-doc-page';
import { LazyIntegrationCreateTemplatePage } from './page/integration/create-template/lazy-integration-create-template-page';
import { LazyIntegrationSendTemplatePage } from './page/integration/send-template/lazy-integration-send-template-page';
import { LazyIntegrationEditTemplatePage } from './page/integration/edit-template/lazy-integration-edit-template-page';
import { LazyIntegrationSendRemindersPage } from './page/integration/send-reminders/lazy-integration-send-reminders-page';
import { LazyIntegrationCancelSignatureRequestPage } from './page/integration/cancel-signature-request/lazy-integration-cancel-signature-request-page';
import { LazyPrepAndSendPage } from './page/prep-and-send/lazy-prep-and-send-page';
import { LazyDropboxLoginVerificationPage } from './page/dropbox/login-verification/lazy-dropbox-login-verification-page';
import { LazyCancelSubscriptionPage } from './page/cancel-subscription/lazy-cancel-subscription-page';
import { LazyNewCancellationFlowPage } from './page/new-cancellation-flow/lazy-new-cancellation-flow-page';
import { LazyMicrosoftSelfSignPage } from './page/microsoft/self-sign/lazy-microsoft-self-sign-page';
import { LazyMicrosoftSendSignaturePage } from './page/microsoft/send-signature/lazy-microsoft-send-signature-page';
import { LazyMicrosoftSettingsPage } from './page/microsoft/settings/lazy-microsoft-settings-page';
import { LazyChooseSignupPlanPage } from './page/choose-signup-plan/lazy-choose-signup-plan-page';
import { LazyThrowAFrontendErrorPleasePage } from './page/throw-a-frontend-error-please/lazy-throw-a-frontend-error-please-page';
import { LazyPaymentPage } from './page/payment/lazy-payment-page';
import { LazyIdentVerificationPage } from './page/ident-verification/lazy-ident-verification-page';
import { LazySignatureRequestsPage } from './page/signature-requests/lazy-signature-requests-page';
import { LazyHelpPage } from './page/help-page/lazy-help-page';
import { LazyAccountSettingsPage } from './page/account-settings/lazy-account-settings-page';
import { LazyProgressiveCheckoutSignUpPage } from './page/authentication/lazy-progressive-checkout-sign-up-page';
import { LazyProgressiveCheckoutSignInPage } from './page/authentication/lazy-progressive-checkout-sign-in-page';
import { LazyJoinTeamPage } from './page/authentication/lazy-join-team-page';
import { LazyNoAccessErrorPage } from './page/no-access-error-page/lazy-no-access-error-page';
import { LazyApiDashboardPage } from './page/api-dashboard/lazy-api-dashboard-page';
import { LazyAcceptTermsPage } from './page/authentication/lazy-accept-terms-page';
import { LazyTemplateConverterPage } from './page/template-converter/lazy-template-converter-page';
import { LazySignFaxBridgePage } from './page/sign-fax-bridge/lazy-sign-fax-bridge-page';
import { LazyVulnerableNewtAuditTrailRenderingIssueReportPage } from './page/one-time-report/lazy-vulnerable-newt-audit-trail-rendering-issue-report-page';
import { LazyCopaceticMoaAttachmentsReportPage } from './page/one-time-report/lazy-copacetic-moa-attachments-report-page';
import { LazyJointVulnerableNewtCopaceticMoaReportPage } from './page/one-time-report/lazy-joint-vulnerable-newt-copacetic-moa-report-page';
import { LazyDocumentPreviewPage } from './page/document-preview/lazy-document-preview-page';

function CancelSubscriptionPageRouter() {
  const isNewCancellationFlowEnabled = useFeatureFlag(
    'sign_core_2026_10_09_new_cancellation_flow',
    false,
  );
  const pageProps = usePageProps('cancelSubscription');
  const isSupportedPlan = ['Essentials', 'Standard', 'Premium'].includes(
    pageProps?.planTier ?? '',
  );

  if (
    isNewCancellationFlowEnabled &&
    (isSupportedPlan || pageProps?.isLegacyPlan === true)
  ) {
    return <LazyNewCancellationFlowPage />;
  }

  return <LazyCancelSubscriptionPage />;
}
function OneTimeReportRouter() {
  const pageKey = useSelector(
    (state: StoreShape) => state.pageProps.data?.pageKey,
  );
  switch (pageKey) {
    case 'vulnerableNewtAuditTrailRenderingIssueReport':
      return <LazyVulnerableNewtAuditTrailRenderingIssueReportPage />;
    case 'copaceticMoaAttachmentsReport':
      return <LazyCopaceticMoaAttachmentsReportPage />;
    case 'jointVulnerableNewtCopaceticMoaReport':
      return <LazyJointVulnerableNewtCopaceticMoaReportPage />;
    default:
      return null;
  }
}
const NotFound = () => {
  // This will trigger our ErrorBoundary and report to Sentry The URL will be
  // part of the report.
  throw new Error('404 not found');
};
// const pathRegex = /\/([^/]+\.php)?/;
const RedirectHome = () => {
  return <RedirectTo url="/" />;
};
function PHPEndpoint() {
  React.useEffect(() => {
    window.location.reload();
  }, []);

  return <></>;
}
/**
 * <Routes holds all the routes for the `hellospa.js` bundle. It should contain
 * ONLY route configuration and should not depend on specific knowledge of the
 * current page.
 *
 *
 * @AppExplorer https://miro.com/app/board/uXjVPXskloA=/?moveToWidget=3458764535231213701&cot=14
 */
// These routes will be accessible by all visitors,
// regardless of whether or not they're logged in.
// <Switch doesn't like react fragments, so we have
// to use an array with keys.
//
// These keys only need to be locally unique.
export const commonRoutes = [
  <Route
    key="FrontendError"
    exact
    path="/info/throwAFrontendErrorPlease"
    component={LazyThrowAFrontendErrorPleasePage}
  />,
  <LabeledRoute
    key="NoAccess"
    label="NoAccess"
    path="/info/noAccess"
    component={LazyNoAccessErrorPage}
  />,
  <LabeledRoute
    key="JoinTeam"
    label="Join team"
    path="/account/confirmTeamInvite"
    component={LazyJoinTeamPage}
  />,
  <LabeledRoute
    key="QesRecovery"
    label="QesRecovery"
    path="/sign/:guid/qes-recovery"
    component={LazyQesRecoveryPage}
  />,
  // The bootstrap carries no user because a transaction number does not authenticate the recipient.
  <LabeledRoute
    key="IdentSuccess"
    label="IdentSuccess"
    path="/info/identSuccess"
    component={LazyIdentVerificationPage}
  />,
  <LabeledRoute
    key="IdentIncomplete"
    label="IdentIncomplete"
    path="/info/identIncomplete"
    component={LazyIdentVerificationPage}
  />,
];

function Routes() {
  const { isDeepIntegration } = useUnsafePageProps();
  const user = useHSUser();
  const siteCode = useSiteCode();
  const HomePage =
    siteCode === siteCodes.fax ? LazyHomeFaxPage : LazyHomeSignPage;

  if (!user) {
    return (
      <Switch>
        {commonRoutes}
        <LabeledRoute
          label="Create an account"
          path="/account/signUp"
          component={LazyProgressiveCheckoutSignUpPage}
        />
        <LabeledRoute
          label="Sign in"
          path="/account/logIn"
          component={LazyProgressiveCheckoutSignInPage}
        />
        <LabeledRoute
          label="Sign in"
          path="/account/mobileLogIn"
          component={LazyProgressiveCheckoutSignInPage}
        />
        <LabeledRoute
          label="Accept terms"
          path="/account/acceptTerms"
          component={LazyAcceptTermsPage}
        />
        <Route path="/dropbox/connect" component={LazyDropboxConnectPage} />
        <Route
          path="/dropbox/logInVerification"
          component={LazyDropboxLoginVerificationPage}
        />
        <RouteBoundary
          name="DropboxConfirmStorageAccess"
          path="/dropbox/confirmStorageAccess"
          component={LazyDropboxConfirmStorageAccessPage}
        />
        <RouteBoundary
          name="DropboxError"
          path="/dropbox/error"
          component={LazyDropboxErrorPage}
        />
        <Route
          path="/dropbox/requestStorageAccess"
          component={LazyDropboxRequestStorageAccessPage}
        />
        <Route path="/dropbox/sso" component={LazyDropboxSsoPage} />
        <Route
          path="/integration/upgradeRequired"
          component={LazyIntegrationWorkflowStatusPage}
        />
        <Route
          path="/integration/enableThirdPartyCookies"
          component={LazyIntegrationWorkflowStatusPage}
        />
        <Route
          path="/integration/status"
          component={LazyIntegrationWorkflowStatusPage}
        />
        <Route
          path="/integration/createOneTimeDoc"
          component={LazyIntegrationCreateOneTimeDocPage}
        />
        <Route
          path="/integration/createTemplate"
          component={LazyIntegrationCreateTemplatePage}
        />
        <Route
          path="/integration/sendTemplate"
          component={LazyIntegrationSendTemplatePage}
        />
        <Route
          path="/integration/editTemplate"
          component={LazyIntegrationEditTemplatePage}
        />
        <Route
          path="/integration/sendReminders"
          component={LazyIntegrationSendRemindersPage}
        />
        <Route
          path="/integration/cancelSignatureRequest"
          component={LazyIntegrationCancelSignatureRequestPage}
        />
        <Route
          path="/microsoft/selfSign"
          component={LazyMicrosoftSelfSignPage}
        />
        <Route
          path="/microsoft/sendSignature"
          component={LazyMicrosoftSendSignaturePage}
        />
        <Route
          path="/microsoft/settings"
          component={LazyMicrosoftSettingsPage}
        />
        <LabeledRoute
          label="RecoverPassword"
          path="/account/recoverPassword"
          component={LazyRecoverPasswordPage}
        />
        <LabeledRoute
          label="NothingToSign"
          path="/info/nothingToSign"
          component={LazyNothingToSignPage}
        />
        <LabeledRoute
          label="NothingToSign"
          path="/sign/:guid"
          component={LazyNothingToSignPage}
        />
        <Route component={NotFound} />
      </Switch>
    );
  } else {
    return (
      <Switch>
        {commonRoutes}
        <LabeledRoute
          label="ApiDashboard"
          path="/apidashboard"
          component={LazyApiDashboardPage}
        />
        <LabeledRoute
          label="AccountSettings"
          path="/accountSettings"
          component={LazyAccountSettingsPage}
        />
        <LabeledRoute
          label="GettingStarted"
          path="/account/gettingStarted"
          component={LazyGettingStartedPage}
        />
        <LabeledRoute
          label="SafetyFirst"
          path="/account/safetyfirst"
          component={LazySafetyFirstPage}
        />
        <LabeledRoute
          label="PostSignUpForm"
          path="/account/postSignUp"
          component={LazyUserPostSignUpPage}
        />
        <LabeledRoute
          label="SetPassword"
          path="/onboarding/setPassword"
          component={LazySetPasswordPage}
        />
        <LabeledRoute
          label="OnboardingUpsell"
          path="/onboarding/upsell"
          component={LazyOnboardingUpsellPage}
        />
        <LabeledRoute
          label="CancelSubscription"
          path="/account/cancelSubscription"
          component={CancelSubscriptionPageRouter}
        />
        <LabeledRoute
          label="EmailActivation"
          path="/account/emailActivation"
          component={LazyInformationFlagPage}
        />
        <LabeledRoute
          label="Admin"
          path="/admin"
          exact
          component={LazyAdminPage}
        />
        <LabeledRoute label="Admin" path="/admin/*" component={LazyAdminPage} />
        <LabeledRoute
          label="Editor"
          path="/editor/prepare"
          component={LazyEditorPage}
        />
        <LabeledRoute
          label="Editor"
          path="/editor/editor"
          component={LazyEditorPage}
        />
        <LabeledRoute label="Home" path="/home/index" component={HomePage} />
        <LabeledRoute
          label="ChooseSignMethod"
          path="/home/chooseSignMethod"
          component={HomePage}
        />
        <LabeledRoute label="Help" path="/home/help" component={LazyHelpPage} />
        <LabeledRoute
          label="Documents"
          path="/home/manage"
          component={LazySignatureRequestsPage}
        />
        <LabeledRoute
          label="HubspotTemplate"
          path="/hubspot/selectTemplate"
          component={LazyHubspotSelectTemplatePage}
        />
        <LabeledRoute
          label="HubspotComplete"
          path="/hubspot/close"
          component={LazyHubspotClosePage}
        />
        <LabeledRoute
          label="ChoosePlan"
          path="/info/chooseSignupPlan"
          exact
          component={LazyChooseSignupPlanPage}
        />
        <LabeledRoute
          label="Payment"
          path="/info/payment"
          component={LazyPaymentPage}
        />
        <LabeledRoute
          label="MoarFaxes"
          path="/internal/moar"
          exact
          component={LazyMoarPage}
        />
        <LabeledRoute
          label="Templates"
          path="/home/createReusableDocs"
          exact
          component={LazyTemplatesPage}
        />
        <LabeledRoute
          label="Templates"
          path="/home/transferTemplates"
          exact
          component={LazyTemplateConverterPage}
        />
        <LabeledRoute
          label="SignFaxBridge"
          path="/signFax/bridge"
          component={LazySignFaxBridgePage}
        />
        <LabeledRoute
          label="OneTimeReport"
          path="/report/:accessToken"
          exact
          component={OneTimeReportRouter}
        />
        <LabeledRoute
          label="DocumentPreview"
          path="/editor/view"
          component={LazyDocumentPreviewPage}
        />
        <LabeledRoute
          label={`prep-and-send${isDeepIntegration ? '.deep-integration' : ''}`}
          path="/prep-and-send/:transmissionGroupGuid"
          component={LazyPrepAndSendPage}
        />

        {/*
          This route can't simply use renderHome, because it has
          page-specific parameters (3rd parameter of prepareSPA). So I have
          to use this awkward hack to make a full page redirect so the BE
          will provide the data needed to render the homepage.

          Accessing this URL from a mobile device with the app will launch
          the app. If a user arrives here without an app or on the desktop,
          it just redirects to the home page.
        */}
        <Route path="/account/mobileApp" component={RedirectHome} />
        <Route path="/template-gallery">
          <TemplateGalleryLayout>
            <LazyTemplateGalleryPage>
              <Switch>
                <Route path="/template-gallery/view/:id">
                  <LazyViewGalleryTemplatePage />
                </Route>
                <Route path="/template-gallery/browse">
                  <LazyBrowseTemplateGalleryPage />
                </Route>
                <Route path="/template-gallery/copy/:id">
                  <PHPEndpoint />
                </Route>
                <Route path="/template-gallery/use/:id">
                  <PHPEndpoint />
                </Route>
                <Route component={NotFound} />
              </Switch>
            </LazyTemplateGalleryPage>
          </TemplateGalleryLayout>
        </Route>
        <Route path="/" exact component={HomePage} />
        <Route component={NotFound} />
      </Switch>
    );
  }
}

export const helloSpaRoutes = <Routes />;
